import { type SWRResponse } from 'swr';

import type ApiError from '@/helpers/ApiError';
import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';
import buildPath from '@/tsClient/utilities/buildPath';

const PATH_TEMPLATE = '/creators/{creatorId}/user-workflows';
export type UserWorkflowsApi = ApiDefinition[typeof PATH_TEMPLATE]['PUT'];

type Input = UserWorkflowsApi['params'] & UserWorkflowsApi['query'] & UserWorkflowsApi['body'];
type Output = UserWorkflowsApi['response'];

const resolveUseUserWorkflowsKey = (params: Input | null): SwrKey => {
  if (!params) return null;
  const {
    initiateMemberIds,
    assignedTeamMemberIds,
    clientTaskFilters,
    endDate,
    personAttorneyIds,
    personId,
    companyId,
    creatorId,
    initiatedByPersonId,
    unassigned,
    page,
    perPage,
    search,
    status,
    statusNot,
    startDate,
    sortDirection,
    sortField,
    filterByCasesInPreparation,
    filterByCasesFiled,
    settlementStatus,
  } = params;

  const data = {
    assignedTeamMemberIds,
    clientTaskFilters,
    companyId,
    endDate,
    filterByCasesFiled,
    filterByCasesInPreparation,
    initiateMemberIds,
    initiatedByPersonId,
    personAttorneyIds,
    personId,
    search,
    settlementStatus,
    sortDirection,
    sortField,
    startDate,
    status,
    statusNot,
    unassigned,
  } satisfies UserWorkflowsApi['body'];

  const requestConfig = {
    data,
    method: 'put' as const,
    params: { page, perPage },
  };

  const path = buildPath<UserWorkflowsApi>(PATH_TEMPLATE, { creatorId });

  return [path, requestConfig];
};

export const useUserWorkflows = (
  params: Input | null,
  config?: UseNoodleSwrConfig<Output>,
): SWRResponse<Output, ApiError> => {
  const key = resolveUseUserWorkflowsKey(params);
  return useNoodleSwr<Output>(key, { revalidateOnFocus: false, ...config });
};

const getUserWorkflows = async ({
  initiateMemberIds,
  assignedTeamMemberIds,
  clientTaskFilters,
  endDate,
  personAttorneyIds,
  personId,
  companyId,
  creatorId,
  initiatedByPersonId,
  unassigned,
  page,
  perPage,
  search,
  status,
  statusNot,
  startDate,
  sortDirection,
  sortField,
  filterByCasesInPreparation,
  filterByCasesFiled,
  settlementStatus,
}: Input): Promise<Output> => {
  const body = {
    assignedTeamMemberIds,
    clientTaskFilters,
    companyId,
    endDate,
    filterByCasesFiled,
    filterByCasesInPreparation,
    initiateMemberIds,
    initiatedByPersonId,
    personAttorneyIds,
    personId,
    search,
    settlementStatus,
    sortDirection,
    sortField,
    startDate,
    status,
    statusNot,
    unassigned,
  };

  const path = buildPath<UserWorkflowsApi>(PATH_TEMPLATE, { creatorId });
  const response = await noodleApiClient.put(path, body, {
    params: {
      page,
      perPage,
    },
  });
  return response.data;
};

export default getUserWorkflows;
